import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { CheckCircle, BookOpen, Pause } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ReadingRowContentProps {
  noteText?: string;
  startedAt?: string;
  progress?: number; // 0-100 percentage
  currentPage?: number;
  totalPages?: number;
  lastUpdated?: string;
  onMarkAsRead?: () => void;
  onUpdateProgress?: () => void;
  onMarkAsUnfinished?: () => void;
  onPauseReading?: () => void;
  loading?: boolean;
}

function relativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return 'some time ago';
  }
}

export function ReadingRowContent({
  noteText,
  startedAt,
  progress = 0,
  currentPage,
  totalPages,
  lastUpdated,
  onMarkAsRead,
  onUpdateProgress,
  onMarkAsUnfinished,
  onPauseReading,
  loading = false
}: ReadingRowContentProps) {
  const hasPageTracking = currentPage !== undefined && totalPages !== undefined && totalPages > 0;
  return (
    <>
      {/* Status and metadata row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="default" className="text-xs bg-green-100 text-green-800 border-green-300">
          Currently Reading
        </Badge>
        
        {startedAt && (
          <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
            Started {relativeTime(startedAt)}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Progress</span>
          {hasPageTracking ? (
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({progress}%)
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">{progress}%</span>
          )}
        </div>
        <Progress value={progress} className="h-2" />
        {lastUpdated && (
          <p className="text-xs text-muted-foreground">
            Updated {relativeTime(lastUpdated)}
          </p>
        )}
      </div>

      {/* Notes */}
      {noteText ? (
        <div className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
          {noteText}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          No reading notes.
        </p>
      )}

      {/* Action buttons - only show if at least one handler is provided */}
      {(onMarkAsRead || onUpdateProgress || onMarkAsUnfinished || onPauseReading) && (
        <TooltipProvider>
          <div className="flex flex-col gap-2 mt-2">
            <div className="flex gap-2">
              {onMarkAsRead && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={onMarkAsRead}
                      disabled={loading}
                      className="flex-1"
                      aria-label="Mark as Read"
                    >
                      <CheckCircle className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                      <span className="hidden sm:inline">Mark as Read</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="sm:hidden">
                    <p>Mark as Read</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {onUpdateProgress && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onUpdateProgress}
                      disabled={loading}
                      className="flex-1"
                      aria-label="Update Progress"
                    >
                      <BookOpen className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                      <span className="hidden sm:inline">Update Progress</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="sm:hidden">
                    <p>Update Progress</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            <div className="flex gap-2">
              {onPauseReading && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onPauseReading}
                  disabled={loading}
                  className="flex-1 text-muted-foreground hover:text-amber-600"
                >
                  <Pause className="w-3 h-3 mr-1" />
                  <span className="text-sm">Pause</span>
                </Button>
              )}
              {onMarkAsUnfinished && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onMarkAsUnfinished}
                  disabled={loading}
                  className="flex-1 text-muted-foreground hover:text-orange-600"
                >
                  <span className="text-sm">Mark as Unfinished</span>
                </Button>
              )}
            </div>
          </div>
        </TooltipProvider>
      )}
    </>
  );
}

export default ReadingRowContent;